import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Formik, Field, FormikProps } from 'formik'
import creditCardType, { CreditCardTypeInfo } from 'credit-card-type'
import { Form } from 'lib/ui'
import { onlyNumbersInterceptor } from 'lib/shared'
import { Container, Grid, SaveCreditCard } from './styled'
import { creditCardFormSchema, creditCardFormValidation } from './schema'

export interface CreditCardData {
  number: string
  expMonth: string
  expYear: string
  cvv: string
  cardType: CreditCardTypeInfo[]
  saveCreditCard?: boolean
}

interface Props {
  onChange: (data: CreditCardData, formik: FormikProps<any>) => any
  allowSaveCreditCard?: boolean
}

export const CreditCardForm: React.FC<Props> = ({
  onChange,
  allowSaveCreditCard = false
}) => {
  const intl = useIntl()
  const validation = creditCardFormValidation(intl)
  const handleSubmit = () => {
    // void
  }
  return (
    <Container>
      <Formik
        initialValues={{
          ...creditCardFormSchema
        }}
        validate={validation}
        onSubmit={handleSubmit}
      >
        {(formikProps) => {
          const cardType = creditCardType(formikProps.values.number)
          const {
            number,
            expMonth,
            expYear,
            cvv,
            saveCreditCard
          } = formikProps.values

          onChange({
            number,
            expMonth,
            expYear,
            cvv,
            cardType,
            saveCreditCard
          }, formikProps)

          return (
            <Form.Wrapper>
              <Field
                name='number'
                label={intl.formatMessage({
                  id: 'checkout.billing.ccForm.label.number'
                })}
                component={Form.Input}
                maxLength='16'
                placeholder='XXXX-XXXX-XXXX-XXXX'
                interceptChange={onlyNumbersInterceptor}
                isRequired
              />

              <Grid>
                <div>
                  <Field
                    name='expMonth'
                    placeholder='Month'
                    label={intl.formatMessage({
                      id: 'checkout.billing.ccForm.label.expMonth'
                    })}
                    component={Form.Select}
                    options={monthOptions}
                    isRequired
                  />
                </div>
                <div className='field'>
                  <Field
                    name='expYear'
                    placeholder='Year'
                    label={intl.formatMessage({
                      id: 'checkout.billing.ccForm.label.expYear'
                    })}
                    component={Form.Select}
                    options={yearOptions}
                    isRequired
                  />
                </div>
                <div>
                  <Field
                    name='cvv'
                    label={intl.formatMessage({
                      id: 'checkout.billing.ccForm.label.cvv'
                    })}
                    component={Form.Input}
                    maxLength='4'
                    interceptChange={onlyNumbersInterceptor}
                    isRequired
                  />
                </div>
              </Grid>
              {allowSaveCreditCard && (
                <SaveCreditCard>
                  <Field
                    name='saveCreditCard'
                    type='checkbox'
                    isRequired
                  />
                  <FormattedMessage
                    id='checkout.billing.ccForm.label.save'
                  />
                </SaveCreditCard>
              )}
            </Form.Wrapper>
          )
        }}
      </Formik>
    </Container>
  )
}

interface Option {
  label: string
  value: string
}

const yearOptions = getYearsOptions()
const monthOptions = getMonthOptions()

function getYearsOptions (): Option[] {
  const options = []
  const lastYear = (new Date().getFullYear())
  for (let i = lastYear; i <= (lastYear + 19); i++) {
    const year = i.toString()
    options.push({
      value: year.substr(-2),
      label: year
    })
  }
  options.unshift({
    value: '',
    label: ''
  })
  return options
}

function getMonthOptions (): Option[] {
  const options = []
  for (let i = 1; i <= 12; i++) {
    const month = `00${i}`.slice(-2)
    options.push({
      value: month,
      label: i.toString()
    })
  }
  options.unshift({
    value: '',
    label: ''
  })
  return options
}
